import React, { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Descriptions, Empty, Input, Modal, Space, Spin, Table, Tag, Tooltip, Typography, message } from 'antd';
import { CaretDownOutlined, CaretRightOutlined, CopyOutlined, ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import request from '../../utils/request';
import TimezoneAwareTime from '../../components/TimezoneAwareTime';
import { getAttachmentUrl } from '../../utils/attachmentUrl';
import { getNonStdContainers, getNonStdStageSnapshots, getNonStdSummary, previewNonStdSettlement } from '../../services/nonstandardOps';
import { ContainerDraft, NonStdStageSnapshotItem, NonStdSummary } from '../../types/nonstandardOps';
import ChargeLinesPanel from '../nonstandard-ops/ChargeLinesPanel';
import FinalAttachmentsPanel from '../nonstandard-ops/FinalAttachmentsPanel';
import LabelsPanel from '../nonstandard-ops/LabelsPanel';
import MediaPreviewModal, { MediaPreviewTarget, previewKindOf } from '../nonstandard-ops/MediaPreviewModal';
import NonStdSnapshotView from '../nonstandard-ops/NonStdSnapshotView';
import SettlementPanel, { SettlementPanelHandle, SettlementStatus } from '../nonstandard-ops/SettlementPanel';
import ContainersPanel, { ContainersPanelHandle, ContainersStatus } from '../nonstandard-ops/ContainersPanel';
import ContainerLabelsAfterSubmit from '../nonstandard-ops/ContainerLabelsAfterSubmit';
import { DestroyRecordsTable, destroyTotalsText } from '../nonstandard-ops/DestroyRecordsPanel';

const { Title, Text } = Typography;

const STATUS_TEXT: Record<string, string> = {
  pending: '待受理', accepted: '已受理', in_progress: '执行中',
  operation_done: '执行阶段已完成（待终审）', completed: '已完成', rejected: '已驳回', cancelled: '已取消',
};

// 和常规指令终审页一致的配色：左列蓝色大卡片 + 白底内部小卡片；右列绿（附件）/ 紫（计费）/ 橙（备注与提交）
const INNER_STYLE = { background: 'rgba(255,255,255,0.9)' };
const RECAP = { header: { background: '#fafafa', borderColor: '#d9d9d9' }, body: { background: '#fafafa', borderColor: '#d9d9d9' } };
const BLUE = { header: { background: '#e6f4ff', borderColor: '#91caff' }, body: { background: '#e6f4ff', borderColor: '#91caff' } };
const GREEN = { header: { background: '#f6ffed', borderColor: '#b7eb8f' }, body: { background: '#f6ffed', borderColor: '#b7eb8f' } };
const PURPLE = { header: { background: '#f9f0ff', borderColor: '#d3adf7' }, body: { background: '#f9f0ff', borderColor: '#d3adf7' } };
const ORANGE = { header: { background: '#fff7e6', borderColor: '#ffd591' }, body: { background: '#fff7e6', borderColor: '#ffd591' } };

/** 子板块的小编号（1.1、2.2 …）：方形淡蓝底，和大区块的圆形序号区分开 */
const subNo = (no: string) => (
  <span
    className="nonstd-review-subno"
    style={{ display: 'inline-block', padding: '0 6px', borderRadius: 4, background: '#e6f4ff', color: '#1677ff', fontSize: 12, fontWeight: 600, lineHeight: '20px' }}
  >
    {no}
  </span>
);

/** 大区块的标题：序号徽标 + 名称 + 一句话性质说明 */
const sectionTitle = (no: number, text: string, note: string, color: string) => (
  <Space size={8} align="center">
    <span
      className="nonstd-review-badge"
      style={{ display: 'inline-flex', width: 22, height: 22, borderRadius: '50%', background: color, color: '#fff', fontSize: 13, alignItems: 'center', justifyContent: 'center' }}
    >
      {no}
    </span>
    <span>{text}</span>
    <span style={{ fontSize: 12, fontWeight: 'normal', color: '#8c8c8c' }}>{note}</span>
  </Space>
);

interface Props {
  instructionId: number;
}

const HELP_TITLE: React.CSSProperties = { fontWeight: 700, margin: '10px 0 2px' };

const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  return data?.detail || (data && typeof data === 'object' ? Object.values(data).flat().join('；') : '') || err?.message || fallback;
};

const snapshotSummary = (item: NonStdStageSnapshotItem): string => {
  const snap: any = item.snapshot;
  if (!snap) return '-';
  if (item.key === 'pick') {
    const stats = snap.data?.stats || {};
    const logs = Array.isArray(snap.data?.pick_logs) ? snap.data.pick_logs : [];
    return `${stats.total_qty ?? 0} 件 · ${stats.kinds ?? '-'} 种 · ${stats.containers ?? '-'} 个容器 · ${logs.length} 条记录`;
  }
  if (item.key === 'record') {
    return `${snap.counts?.entries ?? 0} 条记录 · ${snap.counts?.rounds ?? 0} 轮 · ${snap.counts?.labels ?? 0} 个标签`;
  }
  return `${snap.counts?.total ?? 0} 个容器 · 已就位 ${snap.counts?.placed ?? 0} · 未使用 ${snap.counts?.unused ?? 0}${snap.note ? ' · 有补充说明' : ''}`;
};

/**
 * 库存非标准操作指令的终审页。布局、配色和常规指令的终审工作台一致（左右两列）：
 * 左：最终执行结果明细（库存结算 / 客户需求 / 各阶段快照 / 容器与标签）；右：终审附件、计费事实、备注和提交。
 * 各阶段的执行记录不在这里直接展开，统一通过"各阶段快照"回看。
 */
const NonStdFinalReview: React.FC<Props> = ({ instructionId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [instruction, setInstruction] = useState<any>(null);
  const [summary, setSummary] = useState<NonStdSummary | null>(null);
  const [stageSnapshots, setStageSnapshots] = useState<NonStdStageSnapshotItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // 提交终审后新建了容器：先弹窗提醒打印容器标签，完成后再返回指令列表
  const [afterSubmit, setAfterSubmit] = useState<{ containers: ContainerDraft[]; summary: string } | null>(null);
  const [needsRecalc, setNeedsRecalc] = useState(false);
  const [mediaTarget, setMediaTarget] = useState<MediaPreviewTarget | null>(null);
  const [snapshotView, setSnapshotView] = useState<NonStdStageSnapshotItem | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [containersHelpOpen, setContainersHelpOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // 初次进入页面，所有板块默认收起
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({ request: true, stage: true, labels: true, destroy: true, settlement: true, boxes: true });
  const [settlementStatus, setSettlementStatus] = useState<SettlementStatus | null>(null);
  const [containersStatus, setContainersStatus] = useState<ContainersStatus | null>(null);
  const settlementRef = useRef<SettlementPanelHandle>(null);
  const containersRef = useRef<ContainersPanelHandle>(null);
  const [confirming, setConfirming] = useState(false);
  // 库存结算和终审容器互相影响（容器的对账表来自结算草稿；容器保存会让结算的预览确认作废）：一边变化，另一边重新读取
  const [settlementTick, setSettlementTick] = useState(0);
  const [containersTick, setContainersTick] = useState(0);

  const loadInstruction = useCallback(async () => {
    try {
      const res = await request.get(`/api/v1/instruction/instructions/${instructionId}/`);
      setInstruction(res.data);
      // 指令详情接口没有单独返回 final_review_notes 字段，上次保存的终审备注在 meta.final_review_notes 里（常规终审页也是读这里）
      setNotes(prev => prev || res.data?.final_review_notes || res.data?.meta?.final_review_notes || '');
    } catch (err: any) {
      message.error(errorText(err, '加载指令失败'));
    }
  }, [instructionId]);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await getNonStdSummary(instructionId));
    } catch (err: any) {
      message.error(errorText(err, '加载终审数据失败'));
    }
  }, [instructionId]);

  const loadSnapshots = useCallback(async () => {
    try {
      setStageSnapshots(await getNonStdStageSnapshots(instructionId));
    } catch {
      setStageSnapshots([]);
    }
  }, [instructionId]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadInstruction(), loadSummary(), loadSnapshots()]);
    setLoading(false);
  }, [loadInstruction, loadSummary, loadSnapshots]);

  useEffect(() => { void loadAll(); }, [loadAll]);

  if (loading && !instruction) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;
  if (!instruction) return <Alert type="error" showIcon message="未找到该指令" />;

  const status = String(instruction.status || '');
  const completed = status === 'completed';
  const canSubmit = status === 'operation_done' || completed;
  const viewer = summary?.viewer;
  const toggle = (key: string) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  const collapseButton = (key: string) => (
    <Button size="small" type="text" icon={collapsed[key] ? <CaretRightOutlined /> : <CaretDownOutlined />} onClick={() => toggle(key)} />
  );

  /** 底部"预览并确认终审操作"：库存结算和容器分配一起预览、一起确认。
   * 先把没保存的草稿存好（保存任何一边都会让另一边的版本号加 1，所以每存一边就让另一边同步一次），再打开预览。 */
  const previewAll = async () => {
    setConfirming(true);
    try {
      if (settlementStatus?.dirty) {
        if (!(await settlementRef.current?.save())) return;
        await containersRef.current?.refresh();
      }
      if (containersStatus?.dirty) {
        if (!(await containersRef.current?.save(true))) return;
        await settlementRef.current?.refresh();
      }
      await settlementRef.current?.openPreview();
    } finally {
      setConfirming(false);
    }
  };

  // 和常规终审页一致：提交成功后返回指令列表页（终审、保存、重算都返回），保留列表的筛选条件
  const backToList = () => {
    const path = String(window.location.pathname || '');
    const base = path.startsWith('/dashboard/staff') ? '/dashboard/staff' : (path.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer');
    navigate(`${base}/instructions/list${location.search || ''}`);
  };

  const submit = async (recalc: boolean) => {
    // 库存结算草稿有修改或还没确认时，后端会拒绝提交；这里先提醒，省得白跑一趟
    if (settlementStatus?.dirty) {
      message.warning('库存结算草稿有未保存的修改：请先保存并点下面的“预览并确认终审操作”，或删掉所有转换行（按默认策略释放锁定），再提交终审');
      return;
    }
    if (settlementStatus?.needsConfirm) {
      message.warning('库存结算草稿还没有预览确认：请先点下面的“预览并确认终审操作”，或删掉所有转换行（按默认策略释放锁定），再提交终审');
      return;
    }
    if (containersStatus?.dirty) {
      message.warning('终审容器草稿有未保存的修改：请先保存，再点下面的“预览并确认终审操作”，然后提交终审');
      return;
    }
    if (containersStatus?.hasBlockers) {
      message.warning('终审容器分配还有没解决的问题（见“终审容器分配”里的红色提示），请先解决，或者删掉所有容器（不新建容器），再提交终审');
      return;
    }
    // 提交前先让管理员最后核对一遍：库存结算提交后不能撤销，所以把真正会发生的事写清楚
    const chargeCount = (summary?.charge_lines || []).length;
    const totals = Object.entries(summary?.confirmed_totals || {}).map(([cur, amount]) => `${cur} ${amount}`).join('，');
    const chargeText = chargeCount > 0 ? `${chargeCount} 条（合计 ${totals || '-'}）` : '没有已确认的收费行，不会生成收费记录';
    const destroyCount = Number(summary?.destroy?.totals?.count || 0);
    let body: React.ReactNode;
    if (recalc) {
      body = (
        <div style={{ lineHeight: 1.8 }}>
          <div>将删除并重新生成本指令的非标准收费记录：{chargeText}。</div>
          <div>已绑定账单的收费记录不能重算，会被拒绝。</div>
          <div>不会再改动库存和容器。</div>
        </div>
      );
    } else if (completed) {
      body = (
        <div style={{ lineHeight: 1.8 }}>
          <div>将保存终审备注和终审附件的修改。</div>
          <div>不会重算收费记录，也不会再改动库存和容器。</div>
        </div>
      );
    } else {
      let settleLine: React.ReactNode = '没有设置库存结算：本指令锁定的库存按默认策略释放。';
      // 结算里的销毁合计：没有设置结算就是 0；预览取不到时是 null（不提醒，避免误报）
      let settledDestroy: number | null = 0;
      if (settlementStatus?.hasWork || (containersStatus?.count || 0) > 0) {
        settledDestroy = null;
        try {
          const pv = await previewNonStdSettlement(instructionId);
          const sm = pv.summary || {};
          settledDestroy = Number(pv.source_totals?.destroy ?? 0);
          const parts = [
            `转消耗 ${sm.consume_qty ?? 0} 件（转化和销毁）`,
            `释放锁定 ${sm.release_qty ?? 0} 件`,
            `转业务留存占用 ${sm.hold_qty ?? 0} 件`,
            `转入新库存 ${sm.produce_qty ?? 0} 件`,
          ];
          settleLine = `库存结算将真正应用：${parts.join('，')}${(containersStatus?.count || 0) > 0 ? `；新建容器 ${containersStatus?.count} 个` : ''}。`;
        } catch {
          settleLine = '库存结算将按已确认的预览真正应用，新建容器按容器分配里设置的来建。';
        }
      }
      body = (
        <div style={{ lineHeight: 1.8 }}>
          <div>将完成以下动作：</div>
          <div>· 指令状态置为“已完成”</div>
          <div>· 生成收费记录：{chargeText}</div>
          <div>· {settleLine}</div>
          {destroyCount > 0 && settledDestroy === 0 ? (
            <div className="nonstd-submit-destroy-warning" style={{ marginTop: 6, color: '#cf1322', fontWeight: 600 }}>
              执行阶段记录了销毁（{destroyTotalsText(summary?.destroy?.totals)}），但库存结算里没有填“销毁”，这些库存不会被扣减。如果确实销毁了，请先取消，到库存结算里填写。
            </div>
          ) : null}
          <div style={{ marginTop: 6, color: '#cf1322', fontWeight: 600 }}>库存结算提交后无法撤销，请最后核对一遍。</div>
        </div>
      );
    }
    Modal.confirm({
      title: recalc ? '保存并重算收费记录' : (completed ? '保存终审结果' : '终审提交'),
      okText: recalc ? '保存并重算' : (completed ? '保存' : '确认提交'),
      okButtonProps: { danger: !completed },
      cancelText: '取消',
      width: 560,
      content: body,
      onOk: async () => {
        setSubmitting(true);
        try {
          const res = await request.post(`/api/v1/instruction/instructions/${instructionId}/result/submit/`, {
            final_review_notes: notes,
            ...(recalc ? { recalc_pricing: true } : {}),
          });
          const created = Number(res.data?.charges_created ?? 0);
          let text = recalc ? `已保存并重算收费记录：共 ${created} 条` : (completed ? '终审结果已保存' : `终审已提交：生成收费记录 ${created} 条`);
          let newContainers: ContainerDraft[] = [];
          if (!completed && !recalc) {
            // 把新建的容器编码带在提示里：返回列表后看不到终审页上的容器卡片
            try {
              const view = await getNonStdContainers(instructionId);
              newContainers = (view.containers || []).filter(c => c.applied_container_code && c.applied_container_id);
              const codes = newContainers.map(c => c.applied_container_code);
              if (codes.length > 0) text += `；新建容器 ${codes.length} 个（${codes.join('、')}）`;
            } catch { /* 取不到就只显示收费记录条数 */ }
          }
          message.success(text, 6);
          setNeedsRecalc(false);
          if (newContainers.length > 0) {
            // 新建的容器要先打印标签（容器和编码提交后才有）：弹窗里打印完再返回列表
            setAfterSubmit({ containers: newContainers, summary: `${text}。` });
            return;
          }
          backToList();
        } catch (err: any) {
          message.error(errorText(err, '提交失败'));
          throw err;
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const copyCode = async () => {
    const code = String(instruction.system_code || instructionId || '').trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      message.success('已复制');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      message.error('复制失败');
    }
  };

  const openDetail = () => {
    const base = String(window.location.pathname || '').startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/staff';
    const path = `${base}/instructions/${instructionId}`;
    try {
      const w: any = window;
      if (w?.tabsManager?.openTabByPath) {
        w.tabsManager.openTabByPath(path, { forceNew: true });
        return;
      }
    } catch { /* 退回同窗口跳转 */ }
    navigate(path);
  };

  const projects: any[] = Array.isArray(instruction.projects_detail) ? instruction.projects_detail : [];
  const attachments: any[] = Array.isArray(instruction.attachments_detail) ? instruction.attachments_detail : [];
  const targets: any[] = Array.isArray(instruction.targets_detail) ? instruction.targets_detail : [];
  // 和常规终审页一致：模板名称 · 模板系统编码
  const templateName = String(instruction.template_detail?.name || instruction.template_snapshot?.name || '').trim();
  const templateCode = String(
    instruction.template_detail?.system_code || instruction.template_snapshot?.system_code
    || instruction.template_detail?.code || instruction.template_snapshot?.code || '',
  ).trim();
  const templateText = templateName && templateCode ? `${templateName} · ${templateCode}` : (templateName || templateCode);

  return (
    <div className="final-review-workbench" style={{ padding: 16, width: '100%' }}>
      <style>{`
        .final-review-workbench *:not(.final-review-main-title):not(.final-review-main-title *) {
          font-weight: 400 !important;
        }
        .final-review-workbench .final-review-main-title {
          font-weight: 700 !important;
        }
      `}</style>

      <Card loading={loading && !summary} style={{ marginBottom: 12 }}>
        <Title level={4} className="final-review-main-title" style={{ margin: 0 }}>指令终审工作台</Title>
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ lineHeight: 1.8 }}>
            <Text type="secondary">状态</Text>：<Text strong>{STATUS_TEXT[status] || status}</Text>
            <Text type="secondary" style={{ marginLeft: 12 }}>关联项目</Text>：
            {projects.length > 0 ? (
              <>
                <Tag color="blue" style={{ marginInlineEnd: 0, marginLeft: 4 }}>{projects[0].project_code || projects[0].name || '-'}</Tag>
                {projects.length > 1 ? (
                  <Tooltip
                    title={(
                      <div>
                        <div>全部关联项目（{projects.length} 个）：</div>
                        {projects.map((p: any, i: number) => <div key={`${p.id}-${i}`} style={{ fontFamily: 'monospace' }}>{p.project_code || p.name || '-'}</div>)}
                      </div>
                    )}
                  >
                    <Text type="secondary" style={{ marginLeft: 4, cursor: 'help' }}>+{projects.length - 1}个</Text>
                  </Tooltip>
                ) : null}
              </>
            ) : <Text type="secondary" style={{ marginLeft: 4 }}>-</Text>}
            <Text type="secondary" style={{ marginLeft: 12 }}>指令</Text>：
            <Text style={{ fontFamily: 'monospace', fontWeight: 900, fontSize: 18, lineHeight: '22px', color: '#1677ff' }}>
              {String(instruction.system_code || instructionId)}
            </Text>
            <Button
              size="small"
              type="text"
              aria-label="复制指令编码"
              icon={copied ? <span style={{ fontWeight: 900 }}>√</span> : <CopyOutlined />}
              onClick={() => { void copyCode(); }}
              style={{ marginLeft: 6 }}
            />
            <Button
              size="small"
              style={{ marginLeft: 8, color: '#1677ff', borderColor: '#1677ff', background: '#ffffff' }}
              onClick={openDetail}
            >
              查看指令详情
            </Button>
          </div>
          <Space>
            {templateText ? <Text style={{ marginRight: 30, color: '#fa541c', fontSize: 16 }}>{templateText}</Text> : null}
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => { void loadAll(); }}>刷新</Button>
          </Space>
        </div>
      </Card>

      {!canSubmit ? (
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message="指令还没有完成执行阶段，执行完成（进入待终审）后才能提交终审。现在可以先查看各阶段的快照。" />
      ) : null}
      {needsRecalc ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={'刚才修改或作废的收费行已经生成过收费记录，请点下面的“保存并重算收费记录”，收费记录才会更新。'}
        />
      ) : null}

      {/* 主体：左右两列布局（和常规指令终审一致） */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* 左列：最终执行结果明细 */}
        <div style={{ flex: '1 1 560px', minWidth: 520 }}>
          {/* ① 执行阶段回看：只读，对照着看 */}
          <Card
            className="nonstd-review-recap"
            style={{ border: '2px solid #8c8c8c' }}
            title={sectionTitle(1, '执行阶段回看', '只读 · 仅供对照', '#8c8c8c')}
            styles={RECAP}
          >
            <Card
              type="inner"
              size="small"
              title={<Space size={8} align="center">{subNo('1.1')}<span>客户需求</span></Space>}
              style={{ marginBottom: 10 }}
              styles={{ header: INNER_STYLE, body: INNER_STYLE }}
              extra={collapseButton('request')}
            >
              {!collapsed.request ? (
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="指令名称">{instruction.name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="客户备注"><span style={{ whiteSpace: 'pre-wrap' }}>{instruction.remarks || '-'}</span></Descriptions.Item>
                  <Descriptions.Item label="客户附件">
                    {attachments.length === 0 ? '-' : (
                      <Space wrap>
                        {attachments.map((att: any, index: number) => {
                          const url = getAttachmentUrl(att);
                          const name = att.file_name || att.name || att.original_name || `附件${index + 1}`;
                          // 图片、视频、PDF、Excel/CSV 在弹窗里预览；Word 等其他类型仍在新标签页打开
                          if (previewKindOf(String(url)) || previewKindOf(String(name))) {
                            return <a key={att.id || index} onClick={() => setMediaTarget({ url, name })}>{name}</a>;
                          }
                          return <a key={att.id || index} href={url} target="_blank" rel="noopener noreferrer">{name}</a>;
                        })}
                      </Space>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              ) : null}
            </Card>

            <Card
              type="inner"
              size="small"
              title={<Space size={8} align="center">{subNo('1.2')}<span>仓库操作各阶段快照</span></Space>}
              style={{ marginBottom: 10 }}
              styles={{ header: INNER_STYLE, body: INNER_STYLE }}
              extra={collapseButton('stage')}
            >
              {!collapsed.stage ? (
                <Table
                  size="small"
                  rowKey="key"
                  pagination={false}
                  dataSource={stageSnapshots}
                  locale={{ emptyText: '暂无阶段快照' }}
                  columns={[
                    { title: '阶段', dataIndex: 'title', width: 100 },
                    {
                      title: '摘要',
                      render: (_: any, row: NonStdStageSnapshotItem) => (
                        row.status === 'done' ? snapshotSummary(row) : <span style={{ color: '#8c8c8c' }}>未提交</span>
                      ),
                    },
                    {
                      title: '提交时间',
                      dataIndex: 'finished_at',
                      width: 150,
                      render: (v: string) => (v ? <TimezoneAwareTime time={v} format="YYYY-MM-DD HH:mm" showTimezone={false} /> : '-'),
                    },
                    {
                      title: '操作',
                      width: 110,
                      render: (_: any, row: NonStdStageSnapshotItem) => {
                        if (row.snapshot) return <a onClick={() => setSnapshotView(row)}>查看快照</a>;
                        return row.status === 'done' ? <Tag>无快照</Tag> : <span style={{ color: '#8c8c8c' }}>-</span>;
                      },
                    },
                  ]}
                />
              ) : null}
            </Card>

            <Card
              type="inner"
              size="small"
              title={<Space size={8} align="center">{subNo('1.3')}<span>容器与标签</span></Space>}
              styles={{ header: INNER_STYLE, body: INNER_STYLE }}
              extra={collapseButton('labels')}
            >
              {!collapsed.labels ? (
                <LabelsPanel
                  instructionId={instructionId}
                  instructionCode={instruction.system_code}
                  labels={summary?.labels || []}
                  nextNumber={summary?.label_next_number}
                  groups={summary?.label_groups}
                  lastGroup={summary?.label_last_group}
                  canEdit={false}
                  canPrint={!!summary?.can_print_labels}
                  showPlacement
                  hideCreatedPrinted
                  compact
                  onChanged={() => { void loadSummary(); }}
                />
              ) : null}
            </Card>

            <Card
              type="inner"
              size="small"
              className="nonstd-review-destroy"
              title={(
                <Space size={8} align="center">
                  {subNo('1.4')}
                  <span>销毁记录</span>
                  {(summary?.destroy?.totals?.count || 0) > 0 ? <Tag color="red" className="nonstd-review-destroy-total">{destroyTotalsText(summary?.destroy?.totals)}</Tag> : null}
                </Space>
              )}
              styles={{ header: INNER_STYLE, body: INNER_STYLE }}
              extra={collapseButton('destroy')}
            >
              {!collapsed.destroy ? (
                (summary?.destroy?.records || []).length > 0 ? (
                  <DestroyRecordsTable records={summary?.destroy?.records || []} totals={summary?.destroy?.totals} />
                ) : summary?.destroy?.no_destroy_declared ? (
                  <span style={{ color: '#8c8c8c' }}>已声明本指令没有发生销毁：{summary.destroy.no_destroy_by_name || '-'}</span>
                ) : (
                  <span style={{ color: '#8c8c8c' }}>没有销毁记录</span>
                )
              ) : null}
            </Card>
          </Card>

          {/* ② 终审操作：真正要执行的动作，提交终审时一并生效 */}
          <Card
            className="nonstd-review-actions"
            style={{ marginTop: 16, border: '2px solid #1677ff' }}
            title={sectionTitle(2, '终审操作', '需要你执行 · 提交终审时一并生效', '#1677ff')}
            styles={BLUE}
          >
            <Card
              type="inner"
              size="small"
              title={(
                <Space size={8} align="center">
                  {subNo('2.1')}
                  <span>库存结算执行</span>
                  <Button size="small" onClick={() => setHelpOpen(true)}>说明</Button>
                </Space>
              )}
              style={{ marginBottom: 10 }}
              styles={{ header: INNER_STYLE, body: INNER_STYLE }}
              extra={(
                <Space size={8}>
                  <Tag color={settlementStatus?.color || (completed ? 'success' : 'default')}>
                    {settlementStatus?.text || (completed ? '已按默认策略处理' : '提交终审时释放锁定')}
                  </Tag>
                  {collapseButton('settlement')}
                </Space>
              )}
            >
              {/* 收起时只是藏起来、不卸载：面板里的草稿状态、未保存的修改和给“终审提交”用的状态都要一直在 */}
              <div className="nonstd-review-settlement-body" style={collapsed.settlement ? { display: 'none' } : undefined}>
                <SettlementPanel
                  ref={settlementRef}
                  instructionId={instructionId}
                  onStatus={setSettlementStatus}
                  reloadKey={containersTick}
                  containerCount={containersStatus?.count ?? 0}
                  destroyNote={(summary?.destroy?.totals?.count || 0) > 0 ? destroyTotalsText(summary?.destroy?.totals) : undefined}
                  onChanged={() => { void loadInstruction(); setSettlementTick(tick => tick + 1); }}
                  legacy={(
                    <>
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 10 }}
                        message="提交终审时，创建指令时锁定的库存按系统默认策略释放（不扣减、不转换）。"
                      />
                      {targets.length === 0 ? (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="本指令没有选择库存目标，无需结算" />
                      ) : (
                        <Table
                          size="small"
                          rowKey={(row: any) => String(row.id)}
                          pagination={false}
                          dataSource={targets}
                          columns={[
                            {
                              title: '操作对象',
                              render: (_: any, row: any) => row.inventory_unit_system_barcode
                                || (row.prev_instruction_id ? `历史指令 #${row.prev_instruction_id} 的产出` : '-'),
                            },
                            { title: '类型', dataIndex: 'inventory_unit_type', width: 110, render: (v: string) => v || '-' },
                            { title: '项目', dataIndex: 'project_code', width: 130, render: (v: string) => v || '-' },
                            { title: '锁定数量', dataIndex: 'reserved_qty_for_instruction', width: 90, render: (v: number) => v ?? 0 },
                            { title: '终审处理', width: 150, render: () => '释放锁定（不扣减）' },
                          ]}
                        />
                      )}
                    </>
                  )}
                />
              </div>
            </Card>

            <Card
              type="inner"
              size="small"
              title={(
                <Space size={8} align="center">
                  {subNo('2.2')}
                  <span>终审容器分配</span>
                  <Button size="small" onClick={() => setContainersHelpOpen(true)}>说明</Button>
                </Space>
              )}
              styles={{ header: INNER_STYLE, body: INNER_STYLE }}
              extra={(
                <Space size={8}>
                  <Tag color={containersStatus?.color || 'default'}>{containersStatus?.text || '提交终审时不新建容器'}</Tag>
                  {collapseButton('boxes')}
                </Space>
              )}
            >
              {/* 收起时只是藏起来、不卸载：面板里的草稿状态、未保存的修改和给“终审提交”用的状态都要一直在 */}
              <div className="nonstd-review-boxes-body" style={collapsed.boxes ? { display: 'none' } : undefined}>
                <ContainersPanel
                  ref={containersRef}
                  instructionId={instructionId}
                  reloadKey={settlementTick}
                  labels={summary?.labels || []}
                  onStatus={setContainersStatus}
                  onChanged={() => setContainersTick(tick => tick + 1)}
                />
              </div>
            </Card>

            {/* 整个"终审操作"的预览和确认：库存结算 + 容器分配一起预览、一起确认 */}
            {settlementStatus && settlementStatus.supported && !settlementStatus.applied && settlementStatus.editable ? (() => {
              const anyDirty = !!settlementStatus.dirty || !!containersStatus?.dirty;
              const hasWork = settlementStatus.hasWork || (containersStatus?.count ?? 0) > 0;
              let tag: React.ReactNode;
              if (anyDirty) tag = <Tag color="warning" className="nonstd-review-confirm-tag">有未保存的修改（点“预览并确认终审操作”时会先保存）</Tag>;
              else if (containersStatus?.hasBlockers) tag = <Tag color="error" className="nonstd-review-confirm-tag">容器分配有问题，请先解决</Tag>;
              else if (hasWork && settlementStatus.confirmed) tag = <Tag color="success" className="nonstd-review-confirm-tag">已确认{settlementStatus.confirmedAt ? ` ${dayjs(settlementStatus.confirmedAt).format('MM-DD HH:mm')}` : ''}</Tag>;
              else if (hasWork) tag = <Tag color="warning" className="nonstd-review-confirm-tag">还没有预览确认</Tag>;
              else tag = <span className="nonstd-review-confirm-tag" style={{ color: '#8c8c8c' }}>没有设置任何处理：提交终审时锁定全部释放，不新建容器</span>;
              return (
                <div
                  className="nonstd-review-confirm-bar"
                  style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(255,255,255,0.9)', border: '1px solid #91caff', borderRadius: 6, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}
                >
                  <Button type="primary" className="nonstd-review-confirm-btn" loading={confirming} disabled={!hasWork && !anyDirty} onClick={() => { void previewAll(); }}>
                    预览并确认终审操作
                  </Button>
                  {tag}
                  <span style={{ fontSize: 12, color: '#595959' }}>确认后，点右侧的“终审提交”才会真正生效。</span>
                </div>
              );
            })() : null}
          </Card>
        </div>

        {/* 右列：终审附件 / 计费事实 / 备注与提交 */}
        <div style={{ flex: '1 1 560px', minWidth: 520, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* ③④⑤ 和左列的 ①② 一样：粗边框 + 圆形序号 */}
          <Card
            className="nonstd-review-attachments"
            style={{ border: '2px solid #52c41a' }}
            title={sectionTitle(3, '终审附件', '上传 Excel/PDF/图片/视频/压缩包等', '#52c41a')}
            styles={GREEN}
          >
            <FinalAttachmentsPanel
              instructionId={instructionId}
              attachments={summary?.final_attachments || []}
              entries={summary?.entries || []}
              canEdit={!!summary?.can_edit && !!viewer?.is_admin}
              onChanged={() => { void loadSummary(); }}
            />
          </Card>

          <Card
            className="nonstd-review-charges"
            style={{ border: '2px solid #722ed1' }}
            title={sectionTitle(4, '终审计费', '手动录入 · 收费行即时保存', '#722ed1')}
            styles={PURPLE}
          >
            {viewer?.can_view_charges && summary ? (
              <ChargeLinesPanel
                instructionId={instructionId}
                lines={summary.charge_lines}
                totals={summary.confirmed_totals}
                isAdmin={!!viewer.is_admin}
                canEdit={!!summary.can_edit && !!viewer.is_admin}
                onChanged={opts => {
                  void loadSummary();
                  if (opts?.requiresRecalc) setNeedsRecalc(true);
                }}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="收费由管理员在终审时确定" />
            )}
          </Card>

          <Card
            className="nonstd-review-submit"
            style={{ border: '2px solid #fa8c16' }}
            title={sectionTitle(5, '终审备注与提交', '备注对客户可见 · 提交后生成收费记录', '#fa8c16')}
            styles={ORANGE}
          >
            <Input.TextArea
              rows={5}
              maxLength={2000}
              showCount
              placeholder="可填写对客户/内部的说明（对客户可见，选填）"
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
            {/* 输入框的“已输入/2000”字数提示显示在输入框下面（占 22px 左右），所以按钮行要离输入框远一点，不然会压在字数上 */}
            <div className="nonstd-review-notes-actions" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 34 }}>
              {completed ? (
                <Space>
                  <Button loading={submitting} onClick={() => submit(false)}>保存终审结果（不重算）</Button>
                  <Button type="primary" danger icon={<ThunderboltOutlined />} loading={submitting} onClick={() => submit(true)}>保存并重算收费记录</Button>
                </Space>
              ) : (
                <Button type="primary" danger loading={submitting} disabled={!canSubmit} onClick={() => submit(false)}>终审提交</Button>
              )}
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              终审提交：指令变为"已完成"，并按已确认的收费行生成收费记录（没有已确认收费行就不生成），收费记录可在“财务管理-收费记录”查看。
            </div>
          </Card>
        </div>
      </div>

      <MediaPreviewModal target={mediaTarget} onClose={() => setMediaTarget(null)} />
      <Modal
        title={snapshotView ? `${snapshotView.title}快照 - 指令：${instruction.system_code}` : ''}
        open={!!snapshotView}
        onCancel={() => setSnapshotView(null)}
        footer={[<Button key="close" onClick={() => setSnapshotView(null)}>关闭</Button>]}
        width={980}
        destroyOnClose
      >
        {snapshotView?.snapshot ? <NonStdSnapshotView snapshot={snapshotView.snapshot} targets={targets} /> : null}
      </Modal>
      <Modal
        title="终审容器分配说明"
        open={containersHelpOpen}
        onCancel={() => setContainersHelpOpen(false)}
        footer={[<Button key="close" onClick={() => setContainersHelpOpen(false)}>关闭</Button>]}
        width={760}
      >
        <div style={{ lineHeight: 1.9, maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>
          <div style={HELP_TITLE}>这是做什么</div>
          <div>库存结算只处理库存单元本身。执行完毕后仍在库的库存单元（操作对象拣出后保留的部分，加上新转化出来的部分）物理上放在哪里，要在这里手动建立新的容器并分配，把执行完毕后仍在库的库存单元分配到新建的容器里，账要对齐。</div>

          <div style={HELP_TITLE}>需要分配多少</div>
          <div>对账表列出每个库存单元的“需分配数量”，以及已分配、未分配的数量：</div>
          <div>· 需分配数量 = （拣出数量 − 转为新库存单元 − 销毁，即拣出后仍保留为原库存单元的部分）＋ 作为转化目标新增的数量。拣货不要求拣满，所以按拣出数量算，不按锁定数量算。</div>
          <div>· “转业务留存”是需分配数量里必须放进 IOC 的那一部分，等于库存结算里该库存单元的“转业务留存占用”；需分配数量里扣掉它，剩下的放进 SC。所以：IOC 数量 = 转业务留存，SC 数量 = 需分配数量 − 转业务留存。</div>
          <div>· 全部分配完（未分配都显示 0）才能通过检查；分多了、少了都会有红色提示。</div>

          <div style={HELP_TITLE}>两种容器</div>
          <div>· <b>SC（仓储容器）</b>可以装保留的和新转化的库存单元，同一个 SC 里的库存单元必须属于同一个分拣组；项目异常库存（AIU）要单独放一个 SC。仓位是一段文字，填仓位表里的仓位编码或者临时位置（见下面“仓位”）；类型选“直接存储”时必须是仓位表里已有的仓位。</div>
          <div>· <b>IOC（指令产出容器）</b>只装操作对象，数量必须正好等于“库存锁定处理”里该库存单元的“转业务留存占用”；AIU 可以和其他库存单元混装（例如整箱退回供货商）。终审提交时生成的 IOC 一律是“已封箱”状态（内容已经定好，不再继续装箱）。</div>

          <div style={HELP_TITLE}>怎么用</div>
          <div>1 先在上面的“库存结算”里设置好库存单元的处理（转化、销毁、转业务留存占用），对账表会跟着变化。</div>
          <div>2 点“新建 SC”“新建 IOC”建容器，填容器类型和仓位（IOC 还要填尺寸、重量），再“添加内容”选库存单元并填数量；“装入全部剩余”会把还没分配完的数量装进当前容器。</div>
          <div>3 也可以点“按对账表自动生成”（还没有任何容器时才有）：转业务留存的装一个 IOC，其余按分拣组各装一个 SC，之后可以随意修改。</div>
          <div>· 下面“执行阶段的标签（参考）”列出员工在执行阶段贴的容器标签，点“参考新建 SC / IOC”会把标签上的位置、内容物备注（IOC 还有尺寸和重量）预填到一个新容器里；内容物不会自动分配，需要你自己选库存单元和数量。标签和新建的容器没有对应关系，也不会因为标签是“已消耗”就改变分配。已消耗、未使用的标签灰色显示，不能参考新建。</div>
          <div>4 点“保存容器草稿”，再点“终审操作”区块最底部的“预览并确认终审操作”（库存结算和容器分配一起预览、一起确认，容器的检查结果也在预览里）。确认之后，点页面右侧的“提交终审”，系统才会真正新建容器；任何一步失败都会整体回滚。保存容器会让库存结算已有的确认作废，需要重新预览并确认。</div>

          <div style={HELP_TITLE}>仓位</div>
          <div>· 仓位只是一个文本框，IOC 和 SC 一样：填仓位表里的仓位编码，或者直接填临时位置（例如“收货区临时位”）。文本框旁边的小标签告诉你系统怎么理解：“正式仓位”（和本仓库仓位表里的编码一致，不分大小写）或“临时仓位”（SC 的临时仓位标签是“临时仓位：将记为未上架”，原因见下面）。提交终审时系统会再按仓位表核对一次。</div>
          <div>· <b>IOC</b>：匹配上正式仓位就记为正式仓位，匹配不上就记为文字仓位；两种都能在容器列表里按仓位搜到。</div>
          <div>· <b>SC</b>：SC 只能挂仓位表里的正式仓位。匹配上就记为正式仓位；匹配不上，SC 仍然是“未上架”，你填的临时位置会记在容器备注和移动记录里，以后再走正常的上架流程（这种 SC 按仓位搜不到）。类型选“直接存储”的 SC 必须匹配上正式仓位。</div>
          <div>· 确认之后，如果仓位表里新增了同名仓位，结果会从“临时”变成“正式”，系统会要求重新预览并确认。</div>

          <div style={HELP_TITLE}>不建容器</div>
          <div>· 不建任何容器时，提交终审不新建容器，也不检查容器账。一旦建了容器，就要把需要分配的数量全部分配完。</div>
          <div>· 执行阶段的容器标签只作为参考，和这里新建的容器没有对应关系。</div>
          <div>· 容器只记录库存放在哪里，不改变库存的现存和占用，也不会自动收费。</div>
          <div>· 提交终审后，卡片上会显示生成的容器编码（可点复制），到仓位容器管理页面按编码搜索，就能查看和打印新容器的标签。</div>
        </div>
      </Modal>
      <Modal
        title="库存结算说明"
        open={helpOpen}
        onCancel={() => setHelpOpen(false)}
        footer={[<Button key="close" onClick={() => setHelpOpen(false)}>关闭</Button>]}
        width={760}
      >
        <div style={{ lineHeight: 1.9, maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>
          <div style={HELP_TITLE}>这是做什么</div>
          <div>库存非标准操作指令没有固定的库存动作：员工把做了什么、数量、结果记录在执行记录和容器标签里，由你在终审时手动确定库存怎么结算。这里只处理库存单元本身（数量、库龄、本指令对它的锁定）；容器（上架到仓储容器、放进 IOC）在“终审容器分配”板块进行。</div>

          <div style={HELP_TITLE}>怎么用</div>
          <div>1 表里每一行是本指令锁定的一个库存单元，“指令锁定数”是本指令锁定它的数量。<b>没有变化的库存单元不用管</b>：没改过的行就是全部不变、锁定全部释放（和以前的默认行为一样）。</div>
          <div>2 有变化的库存单元，在“库存单元变化”里填：多少件“转为新库存单元”、多少件“销毁”，其余自动算成“不变”。三者相加等于指令锁定数。</div>
          <div>3 填了“转为新库存单元”的，点这一行的“设置”：点“添加目标库存单元”，每一行选“项目正常库存(非AIU)”（再选项目基础配置里已有的库存单元，不论有没有库存；下拉里显示系统编码和内容项，可以输入系统编码、产品名称、产品编码或分拣条码模糊搜索）或“项目异常库存(AIU)”（项目已有 AIU 会直接显示，没有就会在提交终审时新建），再填转化后的数量；转入 AIU 的行还要填这批 AIU 的总重量（g，必填），系统用它按数量加权更新 AIU 的平均重量。同一个目标只能设置一行，AIU 也只能一行。转化后的数量由你填写，可以比转化前多（例如 1 箱拆成 24 件）。一个库存单元转成多个目标就是拆分。</div>
          <div>4 “库存锁定处理”自动算出（灰底的不能手改）：转为新库存单元和销毁的件数 = “消耗”（库存扣减）；不变的件数 = “释放”。如果不变的部分还要继续占用（例如要出库，见下面“关于出库”），在“转业务留存占用”里填件数，这部分不释放，改成业务留存占用。</div>
          <div>5 多个库存单元合成一个新库存单元（组合）：勾选这几行，点上方“转化为…（选中的合并转化）”，一起设置目标。上方的批量按钮还可以一键设置“不变·释放”“不变·转业务留存占用”“全部销毁库存”。</div>
          <div>6 点“保存草稿”，再点“终审操作”区块最底部的“预览并确认终审操作”（库存结算和容器分配一起预览、一起确认）：预览显示“指令操作对象汇总”（所有操作对象库存单元合计，只统计本指令锁定的那部分：不变 / 转为新库存单元 / 销毁各多少，释放 / 转业务留存占用 / 消耗各多少），以及“转化为的库存单元（新增）”列表（库存单元、内容项、新增数量、库龄；只显示新增，不显示原来的现存和锁定）。有“阻断”必须先改；有“警告”要勾选“我已知晓”才能确认；“提示”只是告知。</div>
          <div>7 确认之后，点页面右侧的“提交终审”，库存才真正变化：系统在同一个事务里先应用库存结算、再生成收费记录，任何一步失败都会整体回滚。确认之后又改了表，要重新预览并确认。</div>

          <div style={HELP_TITLE}>常见场景</div>
          <div>· 全部没问题：什么都不用填，直接提交终审，锁定全部释放。</div>
          <div>· 10 件里 2 件破损要销毁：“销毁”填 2。</div>
          <div>· 10 件里 5 件要退回供货商：这 5 件保持“不变”，并在“转业务留存占用”填 5，到“终审容器分配”把它们放进 IOC；终审提交后，再追加新的出库指令，选这个 IOC 作为操作对象。</div>
          <div>· 10 件里 2 件破损转成异常库存：“转为新库存单元”填 2，点“设置”选“项目异常库存(AIU)”，数量 2。</div>
          <div>· 翻新：A 的 10 件翻新成可售的 B 共 9 件：A 的“转为新库存单元”填 10，目标选 B，数量填 9。</div>
          <div>· 1 箱拆成 24 件：箱的“转为新库存单元”填 1，目标选散货库存单元，数量填 24。</div>
          <div>· A 的 100 件和 B 的 50 件合成 C：勾选 A、B 两行，点“转化为…”，目标选 C，数量填合成后的数量。</div>

          <div style={HELP_TITLE}>关于出库</div>
          <div>· 库存非标准操作指令<b>不直接处理出库</b>：不能把操作对象直接出库，因为出库涉及下架和出库的各种费用，要按出库指令的流程来。</div>
          <div>· 需要出库的库存，先在这里把这部分数量保留为“不变”，并填“转业务留存占用”（这部分库存继续被本指令占用，不会被释放），在“终审容器分配”里把它们放进 IOC；终审提交后，再追加新的出库指令，选这个 IOC 作为操作对象，由出库指令完成下架和出库。</div>
          <div>· 所以“库存单元变化”里只有“不变”“转为新库存单元”“销毁”三种，没有“出库”。</div>

          <div style={HELP_TITLE}>规则和限制</div>
          <div>· 不能超出本指令的锁定数量；转化目标只能是项目基础配置中已经存在的库存单元，或项目的异常库存单元（AIU），并且要属于本指令涉及的项目（指令自己的项目，加上被锁定的库存单元所属的项目）。一组转化的来源可以属于不同的项目（例如两个项目里同一款产品的库存单元转成其中一个项目的库存单元），这时库存的项目归属和仓储费会转到目标所属的项目，预览里会有提示。如果要转为不存在的库存单元，需要先修改项目基础配置，创建这个库存单元。</div>
          <div>· 转化出来的新库存会新建入库层，库龄按被消耗的（转化加销毁）各入库层数量加权；也可以对某个目标手动指定库龄，必须写原因。</div>
          <div>· 结算产生的入库和消耗（转化、销毁）都不会自动收费，收费只来自“终审计费事实”里手动生成的收费行。</div>
          <div>· 库存非标准操作指令不支持以历史指令执行结果（IOC）为操作对象；创建时留下的旧数据提交终审仍按默认策略释放锁定，不做库存结算。</div>
          <div>· 各阶段的执行记录不在这里展开，请在上方“各阶段快照”里查看当时提交的内容。</div>
        </div>
      </Modal>
      <ContainerLabelsAfterSubmit
        open={!!afterSubmit}
        containers={afterSubmit?.containers || []}
        summary={afterSubmit?.summary || ''}
        onDone={() => { setAfterSubmit(null); backToList(); }}
      />
    </div>
  );
};

export default NonStdFinalReview;
